import { Link } from "wouter";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { useState } from "react";

interface FAQItem {
  q: string;
  a: string;
}

const faqSections: { title: string; items: FAQItem[] }[] = [
  {
    title: "General",
    items: [
      {
        q: "What is GamePoint?",
        a: "GamePoint is a skill-based competition platform where anyone can create, join, or promote games in any category imaginable — cooking, coding, fitness, art, trivia, and thousands more. Entry fees build a prize pool, and winners earn real money based on their performance.",
      },
      {
        q: "Is GamePoint gambling?",
        a: "No. GamePoint is a skill-based competition platform. All game outcomes are determined by measurable skill, creativity, knowledge, or community voting — never by random chance. This is similar to how cooking competitions, hackathons, and talent shows work, but with entry fees and cash prizes.",
      },
      {
        q: "Who can use GamePoint?",
        a: "Anyone 18 years or older from any country in the world can use GamePoint (except users in OFAC-sanctioned countries). You can play, create games, or promote games — or do all three at the same time.",
      },
      {
        q: "What types of games can be created?",
        a: "The categories are infinite. If it can be judged fairly, it can be a GamePoint game. Popular categories include cooking, fitness, art, music, coding, photography, trivia, sports, crafts, and more. Our AI Game Creator helps you design games that are legally compliant, clearly structured, and optimized for success.",
      },
    ],
  },
  {
    title: "How It Works",
    items: [
      {
        q: "How does the money work?",
        a: "Players buy tickets (entry fees) to join a game. All entry fees go into a prize pool. GamePoint takes a flat 15% platform fee. The remaining 85% is split between the game creator (their set share), the promoter pool (set by the creator), and the player prize pool. The creator decides the exact percentages and how prizes are distributed across placements.",
      },
      {
        q: "How does the automatic promoter system work?",
        a: "Every time you buy a ticket, you automatically receive a unique referral link for that game. If someone buys a ticket through your link, you become a promoter and earn a pro-rata share of the promoter pool. For example, if 100 tickets are sold through promoter links and you sold 80 of them, you earn 80% of the promoter fund. No sign-up required — just share your link.",
      },
      {
        q: "How are winners determined?",
        a: "It depends on the game. Creators define the judging method when they create the game. Common methods include community voting (all participants or the public vote), expert judging (designated judges score entries), measurable performance (timed events, scored competitions), and objective criteria (specific metrics defined in the rules).",
      },
      {
        q: "What happens if a game is cancelled?",
        a: "If a game is cancelled before completion, all entry fees are refunded in full to players' wallets. GamePoint prioritizes protecting players' money.",
      },
    ],
  },
  {
    title: "For Creators",
    items: [
      {
        q: "How much does it cost to create a game?",
        a: "The base game creation fee is $20. However, there are many ways to reduce this: first-time creators get their first game free, subscription plans include games, referral credits can be applied, and promotional discounts are regularly available. The effective average fee is typically $5-$10.",
      },
      {
        q: "How much can I earn as a creator?",
        a: "Creators set their own share (up to 15% of total entry fees). Small example: $10 tickets x 10 players x 10% creator share = $10. Medium example: $20 tickets x 50 players x 10% = $100. Large example: $20 tickets x 500 players x 10% = $1,000. Use the Creator Earnings Calculator on our homepage to model your specific scenario.",
      },
      {
        q: "What does the AI Game Creator do?",
        a: "The AI helps you design games by evaluating your concept across four priorities: (1) Safety and legal compliance — ensuring your game doesn't involve dangerous or illegal activities; (2) Platform protection — ensuring it meets our terms of service; (3) Game success — making sure rules are clear, judging is fair, and the game won't fail; (4) Viral marketing — optimizing the game for promoter adoption and player engagement. The AI suggests improvements at each step.",
      },
    ],
  },
  {
    title: "For Promoters",
    items: [
      {
        q: "How do I become a promoter?",
        a: "You become a promoter automatically. When you buy a ticket to any game, you receive a unique referral link. Share that link anywhere — social media, messaging apps, email, your website. If someone buys a ticket through your link, you earn a share of the promoter pool. You must be a registered, logged-in user to receive payment.",
      },
      {
        q: "How is the promoter pool split?",
        a: "The promoter pool is split pro-rata based on tickets sold. If a promoter pool is $500 and promoted links sell 100 total tickets, selling 1 ticket earns 1% ($5), selling 10 tickets earns 10% ($50), and selling all 100 earns 100% ($500). The more tickets you sell, the bigger your share.",
      },
      {
        q: "Can I promote games I didn't create?",
        a: "Yes. Any user can promote any game. You just need to buy a ticket first (which gives you your referral link), then share that link with your audience. You can be a player and promoter simultaneously.",
      },
    ],
  },
  {
    title: "Payments & Withdrawals",
    items: [
      {
        q: "How do I get paid?",
        a: "All earnings (prizes, creator shares, promoter commissions) are deposited into your GamePoint wallet. You can withdraw funds to your connected payment method at any time, subject to identity verification for larger amounts.",
      },
      {
        q: "What payment methods are supported?",
        a: "Payments are processed through Stripe, which supports credit/debit cards, bank transfers, and other payment methods depending on your country.",
      },
      {
        q: "Are there withdrawal limits?",
        a: "The minimum withdrawal request is $5. There are no maximum withdrawal limits, but withdrawals over $500 in a rolling 30-day period may require identity verification. Processing usually depends on the payout method and Stripe availability in your country, and banks or payment providers may apply their own fees.",
      },
      {
        q: "How long do withdrawals take?",
        a: "Withdrawal requests are reviewed and processed through the selected payout method. Timing varies by method, country, bank, and whether identity verification is required. Your wallet and withdrawal history show the current request status.",
      },
    ],
  },
  {
    title: "Safety & Trust",
    items: [
      {
        q: "How does GamePoint prevent cheating?",
        a: "We use multiple layers of protection: AI-powered game design that ensures clear, unambiguous rules; community voting with anti-manipulation safeguards; multi-account detection; automated fraud monitoring; and a graduated enforcement system ranging from warnings to permanent bans.",
      },
      {
        q: "What if I see something inappropriate?",
        a: "Use the in-game reporting feature or contact us at abuse@gamepoint.com. All reports are reviewed within 24 hours. Reports involving safety concerns are escalated immediately.",
      },
      {
        q: "How are creator or prize disputes handled?",
        a: "Users can report rule violations, incorrect winners, payment issues, or other concerns from the game page. The team reviews the game rules, submissions, votes, and payment records before deciding whether to uphold results, adjust settlement, cancel a game, or issue refunds.",
      },
      {
        q: "What happens if a tournament must be cancelled?",
        a: "If a tournament is cancelled before settlement, player entry fees are refunded to wallets. Common reasons include unclear rules, creator cancellation, safety issues, or too few valid entries to determine a fair winner.",
      },
      {
        q: "What if I lose access to my account?",
        a: "Use the normal sign-in and verification flow first. If you still cannot access your account, contact support@gamepoint.com with your account email and any relevant tournament or payment details so support can help verify ownership.",
      },
      {
        q: "Is my money safe?",
        a: "Yes. All entry fees are held in escrow until games are completed and settled. Payments are processed through Stripe, a PCI-compliant payment processor. We never store your credit card information on our servers.",
      },
    ],
  },
];

function FAQAccordion({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/20 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
      >
        <span className="font-bold text-foreground text-sm">{item.q}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 text-sm text-muted-foreground leading-relaxed">
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/30 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link href="/">
            <span className="flex items-center gap-2 text-muted-foreground hover:text-neon transition-colors cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              Back to GamePoint
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-extrabold mb-2">Frequently Asked Questions</h1>
        <p className="text-muted-foreground mb-12">Everything you need to know about GamePoint. Can't find what you're looking for? Contact us at support@gamepoint.com.</p>

        <div className="space-y-10">
          {faqSections.map((section, i) => (
            <div key={i}>
              <h2 className="text-xl font-bold text-neon mb-4">{section.title}</h2>
              <div className="glass-panel rounded-xl p-4">
                {section.items.map((item, j) => (
                  <FAQAccordion key={j} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <Link href="/support">
            <span className="text-neon hover:underline cursor-pointer font-bold">Contact Support</span>
          </Link>
        </div>
      </main>
    </div>
  );
}
